import React from "react";
import { Controller } from "react-hook-form";
import { useUploadFiles } from "../../../../shared/components/UploadFiles";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Checkbox from "../../../shared/components/ui/Checkbox";
import { Box } from "../../../shared/components/ui/chakra";
import DeleteIcon from "../../../shared/components/icons/DeleteIcon";
import KeyboardArrowDownIcon from "../../../shared/components/icons/KeyboardArrowDownIcon";
import KeyboardArrowUpIcon from "../../../shared/components/icons/KeyboardArrowUpIcon";
import { Options } from "../../../shared/lib/types";
import {
  ContractorInfoArchitecturalDesignWorksAboveGroundFloorsField,
  ContractorInfoArchitecturalDesignWorksBuildingTypeField,
  ContractorInfoArchitecturalDesignWorksCompletionYearField,
  ContractorInfoArchitecturalDesignWorksImagesField,
  ContractorInfoArchitecturalDesignWorksPrefectureField,
  ContractorInfoArchitecturalDesignWorksProjectDetailField,
  ContractorInfoArchitecturalDesignWorksProjectNameField,
  ContractorInfoArchitecturalDesignWorksTotalFloorAreaField,
  ContractorInfoArchitecturalDesignWorksUndergroundFloorsField,
} from "./FormFields";
import MultiSelectForOptions from "./MultiSelectForOptions";
import { useEditForm } from "../lib/useEditForm";


import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Flex,
  HStack,
  Stack,
} from "@chakra-ui/react";

const DesigendArchitecturalFormForEdit = ({
  title,
  field,
  methods: {
    control,
    formState: { disabled },
  },
  namePrefix,
  buildingTypes,
  structureTypes,
  prefectures,
  onChange,
  onClickRemove = undefined,
  defaultImages = [],
}: {
  title: string;
  methods: ReturnType<typeof useEditForm>;
  field: { project_name: string; published: boolean };
  namePrefix: `contractor_info_attributes.architectural_design_works_attributes.${number}`;
  buildingTypes: Options;
  structureTypes: Options;
  prefectures: Options;
  onChange: (files: File[]) => void;
  onClickRemove?: () => void;
  defaultImages?: { url: string; signed_id: string }[];
}) => {
  // TODO: 新規登録時にはFile[]しかないが、asは使いたくない
  const [files, addFiles, removeFile] = useUploadFiles(
    defaultImages ?? [],
    [],
    3,
    (files) => onChange(files as File[]),
  );
  const isNewItem = field.project_name === "";

  return (
    <Accordion allowToggle={!isNewItem} defaultIndex={0}>
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton
              gap={3}
              bgColor="brand.50"
              p={3}
              _hover={{ bgColor: undefined }}
            >
              <Box
                color="white"
                fontSize="xs"
                bgColor={field.published || isNewItem ? "primary" : "gray.base"}
                px={2}
                py={0.5}
                fontWeight="bold"
                borderRadius={2}
              >
                {isNewItem ? "新規" : field.published ? "公開中" : "非公開"}
              </Box>
              <Box
                flex="1"
                textAlign="left"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {field.project_name}
              </Box>
              {isExpanded ? (
                !isNewItem && (
                  <KeyboardArrowUpIcon boxSize={6} color="primary" />
                )
              ) : (
                <KeyboardArrowDownIcon color="primary" boxSize={6} />
              )}
            </AccordionButton>
            <AccordionPanel p={0} mt={4} position="relative">
              <Box
                w={0}
                h={0}
                borderLeft="6px solid transparent"
                borderRight="6px solid transparent"
                borderBottom="8px solid var(--chakra-colors-brand-50)"
                position="absolute"
                top="-8px"
                left="16px"
              />
              <Box
                as="fieldset"
                py={4}
                px={5}
                borderRadius={2}
                bgColor="brand.50"
              >
                <Stack gap={4}>
                  <Flex justify="space-between">
                    <Box as="legend">
                      <FormLabel>{title}</FormLabel>
                    </Box>
                    {onClickRemove && !disabled && (
                      <HStack
                        gap={0.5}
                        color="text.caution.dark"
                        as="button"
                        _hover={{ bgColor: "background.caution" }}
                        py={1}
                        pl={1}
                        pr={2}
                        onClick={onClickRemove}
                      >
                        <DeleteIcon />
                        <Box mt={0.5} fontSize="xs">
                          実績を削除
                        </Box>
                      </HStack>
                    )}
                  </Flex>
                  <Controller
                    control={control}
                    name={`${namePrefix}.published`}
                    render={({ field: { value, ...field } }) => (
                      <Checkbox {...field} defaultChecked={value}>
                        この実績を公開する
                      </Checkbox>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.project_name`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksProjectNameField
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.building_type_id`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksBuildingTypeField
                        field={field}
                        fieldState={fieldState}
                        buildingTypes={buildingTypes}
                      />
                    )}
                  />
                  <Box as="fieldset">
                    <Box as="legend">
                      <FormLabel required>構造種別</FormLabel>
                    </Box>
                    <Box mt={2}>
                      <Controller
                        control={control}
                        name={`${namePrefix}.structure_type_ids`}
                        render={({
                          field: { ref, value, onChange, disabled, ...field },
                          fieldState: { error },
                        }) => (
                          <Box ref={ref} tabIndex={-1}>
                            <MultiSelectForOptions
                              options={structureTypes}
                              value={value}
                              onChange={onChange}
                              isDisabled={disabled}
                              {...field}
                            />
                            {error?.message && (
                              <InputError>{error.message}</InputError>
                            )}
                          </Box>
                        )}
                      />
                    </Box>
                  </Box>
                  <Controller
                    control={control}
                    name={`${namePrefix}.project_detail`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksProjectDetailField
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.prefecture_id`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksPrefectureField
                        field={field}
                        fieldState={fieldState}
                        prefectures={prefectures}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.above_ground_floors`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksAboveGroundFloorsField
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.underground_floors`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksUndergroundFloorsField
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.total_floor_area`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksTotalFloorAreaField
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${namePrefix}.completion_year`}
                    render={({ field, fieldState }) => (
                      <ContractorInfoArchitecturalDesignWorksCompletionYearField
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                  <Box as="fieldset">
                    <Box as="legend">
                      <FormLabel>建物写真</FormLabel>
                    </Box>
                    <ContractorInfoArchitecturalDesignWorksImagesField
                      files={files}
                      addFiles={addFiles}
                      removeFile={removeFile}
                      isDisabled={disabled}
                    />
                  </Box>
                </Stack>
              </Box>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default DesigendArchitecturalFormForEdit;
